import { MaintenanceGuard } from './guards/maintenance.guard';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OwlDateTimeModule } from 'ng-pick-datetime';
import { OwlMomentDateTimeModule } from 'ng-pick-datetime-moment';
import { DpDatePickerModule } from 'ng2-date-picker';
import { DragScrollModule } from 'ngx-drag-scroll';
import {
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
  PERFECT_SCROLLBAR_CONFIG,
} from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { AppErrorHandler } from './error/app-error.handler';
import { TranslateLocalLoader } from './factories/translate-local-loader';
import { AdminCalendarGuard } from './guards/admin/admin-calendar.guard';
import { AdminCamGuard } from './guards/admin/admin-cam.guard';
import { AdminCommunicationGuard } from './guards/admin/admin-communication.guard';
import { AdminMachineGroupGuard } from './guards/admin/admin-machine-groups.guard';
import { AdminMachineGuard } from './guards/admin/admin-machine.guard';
import { AdminMaterialGuard } from './guards/admin/admin-material.guard';
import { AdminMenuGuard } from './guards/admin/admin-menu.guard';
import { AdminOFGuard } from './guards/admin/admin-of.guard';
import { AdminPresettingGuard } from './guards/admin/admin-presetting.guard';
import { AdminRefrigerationGuard } from './guards/admin/admin-refrigeration.guard';
import { AdminToolGuard } from './guards/admin/admin-tool.guard';
import { AdminUserGuard } from './guards/admin/admin-user.guard';
import { AdminGuard } from './guards/admin/admin.guard';
import { AdministratorGuard } from './guards/administrator.guard';
import { CalendarGuard } from './guards/calendar.guard';
import { CamGuard } from './guards/cam.guard';
import { ChecklistGuard } from './guards/checklist.guard';
import { ConfigurationGuard } from './guards/configuration.guard';
import { DashboardGuard } from './guards/dashboard.guard';
import { LoggedInGuard } from './guards/logged-in.guard';
import { ManufacturingOrdersGuard } from './guards/manufacturingOrders.guard';
import { NotLoggedInGuard } from './guards/not-logged-in.guard';
import { SchedulerGuard } from './guards/scheduler.guard';
import { SigningValidationsGuard } from './guards/signing-validations.guards';
import { ChangeLogGuard } from './guards/change-log.guard';
import { ProductionGuard } from './guards/production.guard';
import { ReadinessGuard } from './guards/readiness.guard';
import { PurchasingGuard } from './guards/purchasing.guard';
import { QualityGuard } from './guards/quality.guard';
import { TechnicalOfficeGuard } from './guards/technical-office.guard';
import { SimulatorGuard } from './guards/simulator.guard';
import { StatusGuard } from './guards/status.guard';
import { ToolManagementGuard } from './guards/tool-management.guard';
import { CheckToolGuard } from './guards/check-tool.guard';
import { ValidationsGuard } from './guards/validations.guard';
import { VisualizatorGuard } from './guards/visualizator.guard';
import { ActivityApiService } from './services/api/activity-api.service';
import { ActivityControlApiService } from './services/api/activity-control-api.service';
import { AdapterApiService } from './services/api/adapter-api.service';
import { AlarmControlApiService } from './services/api/alarm-control-api.service';
import { ApiSocketService } from './services/api/api-socket.service';
import { ArticleApiService } from './services/api/article-api.service';
import { ArticleCamApiService } from './services/api/article-cam-api.service';
import { AssistantApiService } from './services/api/assistant-api.service';
import { AuthApiService } from './services/api/auth-api.service';
import { BaseManufacturingOrderApiService } from './services/api/base-manufacturing-order-api.service';
import { BrandApiService } from './services/api/brand-api.service';
import { CamApiService } from './services/api/cam-api.service';
import { CamToolApiService } from './services/api/cam-tool-api.service';
import { CamToolHolderApiService } from './services/api/cam-tool-holder-api.service';
import { CategoryApiService } from './services/api/category-api.service';
import { ClaimedManufacturingOrderApiService } from './services/api/claimed-manufacturing-order-api.service';
import { ClientApiService } from './services/api/client-api.service';
import { ControlApiService } from './services/api/control-api.service';
import { ErrorApiService } from './services/api/error-api.service';
import { FixedToolMeasurementApiService } from './services/api/fixed-tool-measurement-api.service';
import { GenericAlarmApiService } from './services/api/generic-alarm-api.service';
import { GraphicMenuApiService } from './services/api/graphic-menu-api.service';
import { HistoricalErpSynchronizationApiService } from './services/api/historical-erp-synchronization-api.service';
import { HolidayApiService } from './services/api/holiday-api.service';
import { IncidenceApiService } from './services/api/incidence-api.service';
import { InternalPresettingApiService } from './services/api/internal-presetting-api.service';
import { MachineApiService } from './services/api/machine-api.service';
import { MachineGroupApiService } from './services/api/machine-group-api.service';
import { MachineGroupScheduleApiService } from './services/api/machine-group-schedule-api.service';
import { MachineGroupSectionApiService } from './services/api/machine-group-section-api.service';
import { OperatorApiService } from './services/api/operator-api.service';
import { OperatorCalendarApiService } from './services/api/operator-calendar-api.service';
import { OperatorGroupApiService } from './services/api/operator-group-api.service';
import { OperatorPlanningApiService } from './services/api/operator-planning-api.service';
import { MaintenanceTaskConfigurationApiService } from './services/api/maintenance-task-configuration-api.service';
import { MaintenanceTaskApiService } from './services/api/maintenance-task.api.service';
import { ManufacturingOrderApiService } from './services/api/manufacturing-order-api.service';
import { ManufacturingOrderArticleApiService } from './services/api/manufacturing-order-article-api.service';
import { ManufacturingOrderFeatureApiService } from './services/api/manufacturing-order-feature-api.service';
import { ManufacturingOrderNotificationApiService } from './services/api/manufacturing-order-notification-api.service';
import { MasterTaskApiService } from './services/api/master-task-api.service';
import { MaterialApiService } from './services/api/material-api.service';
import { ModelApiService } from './services/api/model-api.service';
import { ModelVisualizatorApiService } from './services/api/model-visualizator-api.service';
import { NonProductiveManufacturingOrderApiService } from './services/api/non-productive-manufacturing-order-api.service';
import { NonProductiveManufacturingOrderCategoryApiService } from './services/api/non-productive-manufacturing-order-category-api.service';
import { OperationApiService } from './services/api/operation-api.service';
import { OperationModeApiService } from './services/api/operation-mode-api.service';
import { OriginApiService } from './services/api/origin-api.service';
import { PartApiService } from './services/api/part-api.service';
import { PlannerApiService } from './services/api/planner-api.service';
import { PrintApiService } from './services/api/print-api.service';
import { PrivilegeApiService } from './services/api/privilege-api.service';
import { ProcessAlarmApiService } from './services/api/process-alarm-api.service';
import { ProductionManagementsApiService } from './services/api/production-managment-api.service';
import { ProgramApiService } from './services/api/program-api.service';
import { ProofApiService } from './services/api/proof-api.service';
import { ReadModeApiService } from './services/api/read-mode-api.service';
import { RefrigerationApiService } from './services/api/refrigeration-api.service';
import { RefrigerationGroupApiService } from './services/api/refrigeration-group-api.service';
import { RoleApiService } from './services/api/role-api.service';
import { SchedulerTaskByPartApiService } from './services/api/scheduler-task-by-part-api.service';
import { SchedulerTaskByTaskApiService } from './services/api/scheduler-task-by-task-api.service';
import { SimpleRefrigerationApiService } from './services/api/simple-refrigeration-api.service';
import { SourceApiService } from './services/api/source-api.service';
import { SubactivityApiService } from './services/api/subactivity-api.service';
import { SubcategoryApiService } from './services/api/subcategory-api.service';
import { SummaryApiService } from './services/api/summary-api.service';
import { ToolManagementApiService } from './services/api/tool-api-management.service';
import { ToolApiService } from './services/api/tool-api.service';
import { ToolCategoryApiService } from './services/api/tool-category-api.service';
import { ToolHolderApiService } from './services/api/tool-holder-api.service';
import { ToolSettingModeApiService } from './services/api/tool-setting-mode-api.service';
import { UserActivityApiService } from './services/api/user-activity-api.service';
import { UserApiService } from './services/api/user-api.service';
import { UserNotificationApiService } from './services/api/user-notification-api.service';
import { UserRoleApiService } from './services/api/user-role-api.service';
import { UserSubactivityApiService } from './services/api/user-subactivity-api.service';
import { ModelMaintenanceApiService } from './services/api/model-maintenance-api.service';
import { TheoreticalMachineApiService } from './services/api/theoretical-machine-api.service';
import { VibrationPowerModeApiService } from './services/api/vibration-power-mode-api.service';
import { WarehouseMovementsApiService } from './services/api/warehouse-movements-api.service';
import { WebSocketService } from './services/api/web-socket.service';
import { WorkingDayOrderApiService } from './services/api/working-day-order-api.service';
import { WorkingHourApiService } from './services/api/working-hour-api.service';
import { WorkingHourProofApiService } from './services/api/working-hour-proof-api.service';
import { WorkingHoursValidatorApiService } from './services/api/working-hours-validator-api.service';
import { WorkingShiftApiService } from './services/api/working-shift-api.service';
import { ZollerFocusApiService } from './services/api/zoller-focus-api.service';
import { ZollerModeApiService } from './services/api/zoller-mode-api.service';
import { RefreshTokenService } from './services/authentication/refresh-token.service';
import { EmptyResponseInterceptor } from './services/base/interceptors/empty-response.interceptor';
import { ErrorInterceptor } from './services/base/interceptors/error.interceptor';
import { JwtInterceptor } from './services/base/interceptors/jwt.interceptor';
import { PendingInterceptor } from './services/base/interceptors/pending.interceptor';
import { RefreshTokenInterceptor } from './services/base/interceptors/refresh-token.interceptor';
import { PendingService } from './services/base/pending.service';
import { ResolverService } from './services/base/resolver.service';
import { TranslationService } from './services/translation.service';
import { ValidationService } from './services/validation/validation.service';
import { CollapseService } from './services/visual/collapse.service';
import { DashboardModelsKeyService } from './services/visual/dashboardModelsKey.service';
import { SchedulerStatusModelsKeyService } from './services/visual/schedulerStatusModelsKey.service';
import { SwitchDashboardService } from './services/visual/switchDashboard.service';
import { TitlesService } from './services/visual/titles.service';
import { ToasterService } from './services/visual/toaster.service';
import { UserNotificationCounterKeyService } from './services/visual/user-notification-counter-key.service';
import { ValidatorApiService } from './services/api/validator-api.service';
import { FileService } from './services/base/file.service';
import { ToolTemplateApiService } from './services/api/tool-template-api.service';
import { UrgentManufacturingOrderApiService } from './services/api/urgent-manufacturing-order-api.service';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelSpeed: 1,
  wheelPropagation: false,
  minScrollbarLength: null,
};

@NgModule({
  imports: [
    HttpClientModule,
    OwlDateTimeModule,
    OwlMomentDateTimeModule,
    DpDatePickerModule,
    DragScrollModule,
    PerfectScrollbarModule,
    ToastrModule.forRoot({
      autoDismiss: true,
      maxOpened: 1,
      positionClass: 'toast-top-center',
      preventDuplicates: false,
      timeOut: environment.toasterTimeout,
    }),
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useClass: TranslateLocalLoader },
    }),
  ],
  exports: [TranslateModule],
  providers: [
    // Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: PendingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EmptyResponseInterceptor,
      multi: true,
    },
    // Handlers
    { provide: ErrorHandler, useClass: AppErrorHandler },
    // Perfect scroll config
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    // API Services
    ActivityApiService,
    ActivityControlApiService,
    AdapterApiService,
    AlarmControlApiService,
    ArticleApiService,
    ArticleCamApiService,
    AssistantApiService,
    AuthApiService,
    BaseManufacturingOrderApiService,
    BrandApiService,
    CamApiService,
    CamToolApiService,
    CamToolHolderApiService,
    CategoryApiService,
    ControlApiService,
    ClientApiService,
    ErrorApiService,
    FixedToolMeasurementApiService,
    GenericAlarmApiService,
    GraphicMenuApiService,
    HolidayApiService,
    HistoricalErpSynchronizationApiService,
    IncidenceApiService,
    InternalPresettingApiService,
    MaintenanceTaskConfigurationApiService,
    MaintenanceTaskApiService,
    MachineApiService,
    MachineGroupApiService,
    MachineGroupScheduleApiService,
    MachineGroupSectionApiService,
    OperatorApiService,
    OperatorGroupApiService,
    OperatorPlanningApiService,
    OperatorCalendarApiService,
    ManufacturingOrderApiService,
    WarehouseMovementsApiService,
    ManufacturingOrderArticleApiService,
    MasterTaskApiService,
    MaterialApiService,
    NonProductiveManufacturingOrderCategoryApiService,
    SourceApiService,
    ModelApiService,
    ModelMaintenanceApiService,
    ManufacturingOrderFeatureApiService,
    ClaimedManufacturingOrderApiService,
    UrgentManufacturingOrderApiService,
    ManufacturingOrderNotificationApiService,
    NonProductiveManufacturingOrderApiService,
    OperationApiService,
    OperationModeApiService,
    OriginApiService,
    PartApiService,
    PlannerApiService,
    PrintApiService,
    ProcessAlarmApiService,
    ProgramApiService,
    PrivilegeApiService,
    ProofApiService,
    ProductionManagementsApiService,
    RefrigerationApiService,
    RefrigerationGroupApiService,
    ReadModeApiService,
    RoleApiService,
    SchedulerTaskByTaskApiService,
    SchedulerTaskByPartApiService,
    SimpleRefrigerationApiService,
    SubactivityApiService,
    SubcategoryApiService,
    SummaryApiService,
    ToolApiService,
    ToolCategoryApiService,
    ToolSettingModeApiService,
    ToolHolderApiService,
    ToolManagementApiService,
    ZollerFocusApiService,
    ZollerModeApiService,
    UserApiService,
    UserActivityApiService,
    UserSubactivityApiService,
    UserNotificationApiService,
    UserRoleApiService,
    ValidatorApiService,
    TheoreticalMachineApiService,
    VibrationPowerModeApiService,
    WebSocketService,
    ApiSocketService,
    WorkingShiftApiService,
    WorkingHourApiService,
    WorkingHoursValidatorApiService,
    WorkingDayOrderApiService,
    WorkingHourProofApiService,
    ModelVisualizatorApiService,
    ToolTemplateApiService,
    // Services
    CollapseService,
    SwitchDashboardService,
    SchedulerStatusModelsKeyService,
    UserNotificationCounterKeyService,
    DashboardModelsKeyService,
    PendingService,
    RefreshTokenService,
    ResolverService,
    TitlesService,
    ToasterService,
    TranslationService,
    ValidationService,
    FileService,
    // Guards
    AdministratorGuard,
    CamGuard,
    VisualizatorGuard,
    ChecklistGuard,
    ConfigurationGuard,
    DashboardGuard,
    LoggedInGuard,
    ManufacturingOrdersGuard,
    MaintenanceGuard,
    NotLoggedInGuard,
    SchedulerGuard,
    SimulatorGuard,
    PurchasingGuard,
    TechnicalOfficeGuard,
    QualityGuard,
    ProductionGuard,
    ReadinessGuard,
    ChangeLogGuard,
    ToolManagementGuard,
    CheckToolGuard,
    StatusGuard,
    ValidationsGuard,
    CalendarGuard,
    SigningValidationsGuard,
    AdminGuard,
    AdminCalendarGuard,
    AdminCamGuard,
    AdminCommunicationGuard,
    AdminMachineGuard,
    AdminMachineGroupGuard,
    AdminMaterialGuard,
    AdminMenuGuard,
    AdminOFGuard,
    AdminRefrigerationGuard,
    AdminToolGuard,
    AdminUserGuard,
    AdminPresettingGuard,
  ],
})
export class CoreModule {}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/core.module.ts